/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const formatWhen = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const formatTime = (dateStr) => new Date(dateStr).toLocaleString(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
});

const getInitials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map(part => part[0]?.toUpperCase()).join('');
};

/**
 * The facility chat groups the signed-in user participates in, as an embeddable
 * dashboard panel with a compact message thread. Group management (creating,
 * renaming, adding people) stays on the dedicated Chats page.
 */
const FacilityChatsPanel = ({ user, onUnreadTotalChange, manageLink }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const messagesContainerRef = useRef(null);
  const threadRef = useRef(null);
  const userId = (user?._id || user?.id)?.toString();

  const fetchChats = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/facility-chats');
      setChats(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching facility chats:', err);
      setError('Failed to load chats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChats();
    const interval = setInterval(fetchChats, 30000);
    return () => clearInterval(interval);
  }, [fetchChats]);

  useEffect(() => {
    if (!onUnreadTotalChange) return;
    onUnreadTotalChange(chats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0));
  }, [chats, onUnreadTotalChange]);

  const fetchMessages = useCallback(async (chatId) => {
    try {
      setMessagesLoading(true);
      const { data } = await axios.get(`/api/facility-chats/${chatId}/messages?limit=50`);
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      toast.error('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedChatId) return;
    fetchMessages(selectedChatId);
    axios.patch(`/api/facility-chats/${selectedChatId}/read`)
      .then(() => fetchChats())
      .catch(() => {});
  }, [selectedChatId, fetchMessages, fetchChats]);

  // The thread opens in place, below the list, so bring the whole mini window
  // into view instead of leaving the user to scroll for it. The message area is
  // a fixed height, so the thread is already its full size on first paint.
  useEffect(() => {
    if (!selectedChatId) return;
    const thread = threadRef.current;
    if (!thread) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(() => {
      thread.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
    });
  }, [selectedChatId]);

  useEffect(() => {
    if (messagesLoading || messages.length === 0) return;
    const container = messagesContainerRef.current;
    if (container) {
      requestAnimationFrame(() => { container.scrollTop = container.scrollHeight; });
    }
  }, [messages, messagesLoading]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !selectedChatId) return;
    try {
      setSending(true);
      await axios.post(`/api/facility-chats/${selectedChatId}/messages`, {
        content: newMessage.trim()
      });
      setNewMessage('');
      await fetchMessages(selectedChatId);
      fetchChats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const selectedChat = chats.find(c => c._id === selectedChatId) || null;

  if (loading) {
    return <div className="home-notif-empty">Loading chats...</div>;
  }

  if (error) {
    return <div className="home-notif-empty">{error}</div>;
  }

  if (chats.length === 0) {
    return (
      <div className="home-notif-empty">
        You are not in any chats yet.
        {manageLink && <> Create one from the <a href={manageLink}>Chats</a> page.</>}
      </div>
    );
  }

  if (selectedChat) {
    return (
      <div className="home-chat-thread" ref={threadRef}>
        <div className="home-chat-thread-header">
          <button type="button" className="home-chat-back-btn" onClick={() => setSelectedChatId(null)}>
            &#8592; All chats
          </button>
          <div className="home-chat-thread-title">
            {selectedChat.name}
            <span className="home-chat-thread-subtitle">
              {selectedChat.facility?.facility_name || ''}
              {selectedChat.participants?.length ? ` · ${selectedChat.participants.length} members` : ''}
            </span>
          </div>
          {manageLink && (
            <Link
              className="home-chat-open-full"
              to={`${manageLink}?chat=${selectedChat._id}`}
              title="Open this chat on the full Chats page"
            >
              Open in Chats
            </Link>
          )}
        </div>

        <div className="home-chat-messages" ref={messagesContainerRef}>
          {messagesLoading ? (
            <div className="home-notif-empty">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="home-notif-empty">No messages yet. Start the conversation.</div>
          ) : (
            messages.map(msg => {
              const isMine = (msg.sender?._id || msg.sender)?.toString() === userId;
              return (
                <div key={msg._id} className={`home-chat-message ${isMine ? 'mine' : ''}`}>
                  {!isMine && (
                    <div className="home-chat-avatar">{getInitials(msg.sender_name)}</div>
                  )}
                  <div className="home-chat-bubble">
                    {!isMine && <div className="home-chat-bubble-sender">{msg.sender_name}</div>}
                    <div className="home-chat-bubble-text">{msg.content}</div>
                    <div className="home-chat-bubble-time">{formatTime(msg.createdAt)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <form className="home-chat-composer" onSubmit={handleSend}>
          <textarea
            className="home-chat-input"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write a message..."
            rows={2}
            maxLength={2000}
          />
          <button
            type="submit"
            className="home-chat-send-btn"
            disabled={!newMessage.trim() || sending}
          >
            {sending ? '...' : 'Send'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="home-chat-list">
      {chats.map(chat => (
        /* The row is a container, not a button, so it can hold both actions:
           "Open" shows the chat here on the dashboard, "Open in Chats" leaves
           for the full page with this chat already selected. */
        <div
          key={chat._id}
          className={`home-chat-item ${chat.unreadCount > 0 ? 'unread' : ''}`}
        >
          <button
            type="button"
            className="home-chat-item-main"
            onClick={() => setSelectedChatId(chat._id)}
          >
            <div className="home-chat-item-title-row">
              <span className="home-chat-item-title">{chat.name}</span>
              {chat.unreadCount > 0 && (
                <span className="home-section-badge unread">{chat.unreadCount}</span>
              )}
            </div>
            <div className="home-chat-item-preview">
              {chat.last_message_preview || 'No messages yet'}
            </div>
          </button>
          <div className="home-chat-item-side">
            <span className="home-chat-item-time">{formatWhen(chat.last_message_at)}</span>
            <div className="home-chat-item-actions">
              <button
                type="button"
                className="home-chat-item-action"
                onClick={() => setSelectedChatId(chat._id)}
              >
                Open
              </button>
              {manageLink && (
                <Link
                  className="home-chat-item-action"
                  to={`${manageLink}?chat=${chat._id}`}
                >
                  Open in Chats
                </Link>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FacilityChatsPanel;
